import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';

/**
 * Route transition — BUILD-PLAN §47 ("page transitions"), §6, §40.
 *
 * This is the one place Framer Motion earns its weight. An *exit* animation
 * requires keeping the outgoing tree mounted after React has already replaced
 * it, which CSS transitions and IntersectionObserver cannot do — everything
 * else in the app uses the lighter `useReveal` hook instead (§6 "do not
 * introduce unnecessary libraries").
 *
 * `mode="wait"` lets the old page finish leaving before the new one arrives,
 * which avoids the two-pages-overlapping flash that makes crossfades look
 * cheap. The durations are deliberately short: a transition the visitor has
 * to wait through is a slow site, not a luxurious one.
 */
export function PageTransition({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const reducedMotion = useReducedMotion();

  // §40/§47: honour the OS setting by rendering with no animation at all,
  // rather than animating faster.
  if (reducedMotion) {
    return <>{children}</>;
  }

  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        // Keying on pathname (not search) means filtering the shop does not
        // re-animate the page on every checkbox.
        key={pathname}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -6 }}
        transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
      >
        {children}
      </motion.div>
    </AnimatePresence>
  );
}
